import * as XLSX from 'xlsx';
import { extractTextRowsFromPdfFile, isPdfFile } from './pdfImageService';

const HEADER_ALIASES = {
  sourceType: [
    'type',
    'itemtype',
    'saletype',
    'transactiontype'
  ],
  serviceName: [
    'item',
    'itemname',
    'itemsold',
    'serviceitem',
    'servicename',
    'treatmentname',
    'service',
    'treatment',
    'description'
  ],
  category: [
    'category',
    'servicecategory',
    'treatmentcategory',
    'type',
    'department',
    'group'
  ],
  date: [
    'saledate',
    'appointmentdate',
    'bookingdate',
    'servicedate',
    'transactiondate',
    'date',
    'datetime'
  ],
  amount: [
    'totalsales',
    'netsales',
    'grosssales',
    'amountdue',
    'saleamount',
    'transactionamount',
    'price',
    'revenue',
    'amount',
    'paid',
    'value',
    'gross',
    'total'
  ],
  vatAmount: [
    'taxesonnetsales',
    'taxes',
    'taxamount',
    'vatamount',
    'salestax',
    'sales_tax',
    'vat',
    'tax',
    'gst',
    'iva'
  ]
};

const BANK_HEADER_ALIASES = {
  date: [
    'date',
    'transactiondate',
    'valuedate',
    'posteddate',
    'bookingdate',
    'bookdate'
  ],
  description: [
    'description',
    'details',
    'narration',
    'reference',
    'merchant',
    'payee',
    'transactiondescription'
  ],
  debit: [
    'debit',
    'withdrawal',
    'moneyout',
    'paidout',
    'outflow',
    'dr'
  ],
  credit: [
    'credit',
    'deposit',
    'moneyin',
    'paidin',
    'inflow',
    'cr'
  ],
  amount: [
    'amount',
    'transactionamount',
    'value',
    'netamount'
  ],
  balance: [
    'balance',
    'runningbalance',
    'availablebalance',
    'currentbalance'
  ]
};

const DEFAULT_COLUMN_MAP = {
  sourceType: -1,
  serviceName: 0,
  category: 1,
  date: 2,
  amount: 3,
  vatAmount: 4
};

const PDF_STATEMENT_SOURCE_VARIANT = 'pdf_statement';

const normalizeHeaderKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const hasMeaningfulValue = (value) =>
  value !== null &&
  value !== undefined &&
  String(value).trim() !== '';

const formatDateAsIso = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const extractIsoDateFromFileName = (fileName = '') => {
  const rawName = String(fileName || '');
  if (!rawName) return null;

  const ymdMatch = rawName.match(/(\d{4})[-_.](\d{2})[-_.](\d{2})/);
  if (ymdMatch) {
    const candidate = `${ymdMatch[1]}-${ymdMatch[2]}-${ymdMatch[3]}`;
    const parsed = parseDateValue(candidate);
    if (parsed) return parsed;
  }

  const dmyMatch = rawName.match(/(\d{2})[-_.](\d{2})[-_.](\d{4})/);
  if (dmyMatch) {
    const candidate = `${dmyMatch[3]}-${dmyMatch[2]}-${dmyMatch[1]}`;
    const parsed = parseDateValue(candidate);
    if (parsed) return parsed;
  }

  return null;
};

const parseDateValue = (value) => {
  if (!hasMeaningfulValue(value)) return null;

  if (value instanceof Date) {
    return formatDateAsIso(value);
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsedExcelDate = XLSX.SSF.parse_date_code(value);
    if (parsedExcelDate?.y && parsedExcelDate?.m && parsedExcelDate?.d) {
      const excelDate = new Date(
        parsedExcelDate.y,
        parsedExcelDate.m - 1,
        parsedExcelDate.d
      );
      return formatDateAsIso(excelDate);
    }

    const timestampCandidate = value > 1000000000000 ? value : value * 1000;
    const timestampDate = new Date(timestampCandidate);
    return formatDateAsIso(timestampDate);
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const ymdMatch = raw.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (ymdMatch) {
    const parsed = new Date(Number(ymdMatch[1]), Number(ymdMatch[2]) - 1, Number(ymdMatch[3]));
    return formatDateAsIso(parsed);
  }

  const dmyOrMdyMatch = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
  if (dmyOrMdyMatch) {
    const first = Number(dmyOrMdyMatch[1]);
    const second = Number(dmyOrMdyMatch[2]);
    const rawYear = Number(dmyOrMdyMatch[3]);
    const year = rawYear < 100 ? rawYear + 2000 : rawYear;

    let day = first;
    let month = second;
    if (first <= 12 && second > 12) {
      month = first;
      day = second;
    } else if (first > 12 && second <= 12) {
      month = second;
      day = first;
    }

    const parsed = new Date(year, month - 1, day);
    return formatDateAsIso(parsed);
  }

  // Support textual month formats often used by Fresha exports,
  // e.g. "26 Feb 2026, 10:55am" or "26 February 2026".
  const dayMonthNameMatch = raw.match(
    /^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})(?:,\s*\d{1,2}:\d{2}\s*[AaPp][Mm])?$/
  );
  if (dayMonthNameMatch) {
    const day = Number(dayMonthNameMatch[1]);
    const monthToken = dayMonthNameMatch[2].slice(0, 3).toLowerCase();
    const year = Number(dayMonthNameMatch[3]);
    const monthIndexByToken = {
      jan: 0,
      feb: 1,
      mar: 2,
      apr: 3,
      may: 4,
      jun: 5,
      jul: 6,
      aug: 7,
      sep: 8,
      oct: 9,
      nov: 10,
      dec: 11
    };
    const monthIndex = monthIndexByToken[monthToken];
    if (Number.isInteger(monthIndex)) {
      const parsed = new Date(year, monthIndex, day);
      return formatDateAsIso(parsed);
    }
  }

  const directDate = new Date(raw);
  return formatDateAsIso(directDate);
};

const parseNumberValue = (value) => {
  if (!hasMeaningfulValue(value)) return null;

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const negativeWrapped = /^\(.*\)$/.test(raw);
  const alphaTokens = (raw.match(/[A-Za-z]+/g) || []).map((token) => token.toUpperCase());
  if (alphaTokens.length > 0) {
    const allowedTokens = new Set([
      'CR',
      'DR',
      'EUR',
      'USD',
      'GBP',
      'AUD',
      'CAD',
      'NZD',
      'JPY',
      'CHF',
      'SEK',
      'NOK',
      'DKK',
      'PLN'
    ]);
    const hasUnexpectedToken = alphaTokens.some((token) => !allowedTokens.has(token));
    if (hasUnexpectedToken) {
      return null;
    }
  }

  let cleaned = raw
    .replace(/[()]/g, '')
    .replace(/\s/g, '')
    .replace(/[A-Za-z]/g, '')
    .replace(/[^\d,.-]/g, '');

  // Keep a single leading sign only.
  const hasNegativeSign = cleaned.startsWith('-');
  cleaned = cleaned.replace(/-/g, '');
  if (!cleaned) return null;

  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');

  if (hasComma && hasDot) {
    // Decide decimal separator by whichever appears last.
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    if (lastComma > lastDot) {
      // "1.234,56" -> "1234.56"
      cleaned = cleaned.replace(/\./g, '').replace(/,/g, '.');
    } else {
      // "1,234.56" -> "1234.56"
      cleaned = cleaned.replace(/,/g, '');
    }
  } else if (hasComma && !hasDot) {
    // If only comma is present, treat it as decimal separator.
    cleaned = cleaned.replace(/,/g, '.');
  } else if (hasDot) {
    // Remove repeated dots except the last decimal dot.
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      const decimal = parts.pop();
      cleaned = `${parts.join('')}.${decimal}`;
    }
  }

  if (hasNegativeSign) {
    cleaned = `-${cleaned}`;
  }

  const parsed = Number.parseFloat(cleaned);
  if (!Number.isFinite(parsed)) return null;
  return negativeWrapped ? -Math.abs(parsed) : parsed;
};

const isEffectivelyEmptyRow = (row = []) =>
  !row.some((cell) => hasMeaningfulValue(cell));

const isLikelyStatementNoiseLine = (line = '') => {
  const normalized = String(line || '').trim().toLowerCase();
  if (!normalized) return true;
  return (
    /^(page\s+\d+|statement\s+period|account\s+(number|name)|sort\s+code|iban|bic)\b/i.test(normalized) ||
    /\b(opening|closing|available)\s+balance\b/i.test(normalized) ||
    (
      /\b(date|description|details|money\s+in|money\s+out|balance|debit|credit|amount)\b/i.test(normalized) &&
      !/^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/.test(normalized)
    )
  );
};

const PDF_DATE_AT_START_PATTERN =
  /^(\d{1,2}[/-]\d{1,2}[/-]\d{2,4}|\d{4}[/-]\d{1,2}[/-]\d{1,2}|\d{1,2}\s+[A-Za-z]{3,9}\s+\d{2,4})\b\s*(.*)$/;

const PDF_AMOUNT_TOKEN_PATTERN =
  /(?:[£$€]\s*)?-?\(?\d{1,3}(?:[,\s]\d{3})*(?:[.,]\d{2})\)?(?:\s*(?:CR|DR))?|(?:[£$€]\s*)?-?\(?\d+(?:[.,]\d{2})\)?(?:\s*(?:CR|DR))?/gi;

const stripPdfAmountTokensFromEnd = (value = '', amountTokens = []) => {
  let output = String(value || '').trim();
  [...amountTokens].reverse().forEach((token) => {
    const escapedToken = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    output = output.replace(new RegExp(`\\s*${escapedToken}\\s*$`, 'i'), '').trim();
  });
  return output;
};

const parsePdfAmountToken = (token = '') => {
  const raw = String(token || '').trim();
  if (!raw) return null;
  const amount = parseNumberValue(raw);
  if (!Number.isFinite(amount)) return null;

  if (/\bDR\b/i.test(raw)) return -Math.abs(amount);
  if (/\bCR\b/i.test(raw)) return Math.abs(amount);
  return amount;
};

const chooseSignedAmountFromPdfTokens = (tokens = []) => {
  const parsedTokens = tokens
    .map((token) => ({
      raw: token,
      amount: parsePdfAmountToken(token)
    }))
    .filter((token) => Number.isFinite(token.amount));

  if (parsedTokens.length === 0) return null;

  const explicitSignedToken = [...parsedTokens].reverse().find((token) =>
    /^-|\b(CR|DR)\b/i.test(String(token.raw || '').trim())
  );
  if (explicitSignedToken) return explicitSignedToken.amount;

  if (parsedTokens.length >= 3) {
    const debit = Math.abs(parsedTokens[parsedTokens.length - 3].amount);
    const credit = Math.abs(parsedTokens[parsedTokens.length - 2].amount);
    if (debit > 0 && credit === 0) return -debit;
    if (credit > 0 && debit === 0) return credit;
  }

  if (parsedTokens.length >= 2) {
    return parsedTokens[parsedTokens.length - 2].amount;
  }

  return parsedTokens[0].amount;
};

const normalizePdfStatementLines = (textRows = []) => {
  const sourceLines = (Array.isArray(textRows) ? textRows : [])
    .map((row, index) => ({
      rowNumber: index + 1,
      text: String(row?.text || row || '').replace(/\s{2,}/g, ' ').trim()
    }))
    .filter((row) => row.text);

  const statementLines = [];
  sourceLines.forEach((line) => {
    if (isLikelyStatementNoiseLine(line.text)) return;

    if (PDF_DATE_AT_START_PATTERN.test(line.text)) {
      statementLines.push({ ...line });
      return;
    }

    const previousLine = statementLines[statementLines.length - 1];
    if (previousLine && !PDF_DATE_AT_START_PATTERN.test(line.text)) {
      previousLine.text = `${previousLine.text} ${line.text}`.replace(/\s{2,}/g, ' ').trim();
    }
  });

  return statementLines;
};

const getPdfRowItems = (row = {}) =>
  (Array.isArray(row?.items) ? row.items : [])
    .map((item) => ({
      text: String(item?.text || item?.str || '').trim(),
      x: Number(item?.x) || 0
    }))
    .filter((item) => item.text)
    .sort((a, b) => a.x - b.x);

const joinPdfItemText = (items = []) =>
  items
    .map((item) => item.text)
    .join(' ')
    .replace(/\s{2,}/g, ' ')
    .trim();

const parsePdfAmountFromItems = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  const raw = items
    .map((item) => String(item?.text || '').trim())
    .filter(Boolean)
    .join('')
    .replace(/([.,])(?=\d{2}\b)/, '.$1'.slice(0, 1))
    .replace(/\s+/g, '');

  return parsePdfAmountToken(raw);
};

const parsePositionedPdfBankStatementRows = (textRows = []) => {
  const rows = Array.isArray(textRows) ? textRows : [];
  const hasPositionedRows = rows.some((row) => getPdfRowItems(row).length > 0);
  if (!hasPositionedRows) return null;

  const parsedRows = [];
  const skippedRows = [];
  let currentDate = null;
  let currentPageNumber = null;
  let isInsideTransactionTable = false;

  rows.forEach((row, index) => {
    const items = getPdfRowItems(row);
    if (items.length === 0) return;

    const pageNumber = row?.pageNumber ?? null;
    if (pageNumber !== currentPageNumber) {
      currentPageNumber = pageNumber;
      currentDate = null;
      isInsideTransactionTable = false;
    }

    const rowText = joinPdfItemText(items);
    const lowerText = rowText.toLowerCase();

    if (
      /\bdate\b/i.test(rowText) &&
      /\bdetails\b/i.test(rowText) &&
      /\bdebit\b/i.test(rowText) &&
      /\bcredit\b/i.test(rowText) &&
      /\bbalance\b/i.test(rowText)
    ) {
      isInsideTransactionTable = true;
      return;
    }

    if (
      /\bfor\s+important\s+information\b/i.test(rowText) ||
      /\boverdrawn\s+balances\s+are\s+marked\s+dr\b/i.test(rowText) ||
      /\bthank\s+you\s+for\s+banking\b/i.test(rowText)
    ) {
      isInsideTransactionTable = false;
      return;
    }

    if (!isInsideTransactionTable) return;

    const dateText = joinPdfItemText(items.filter((item) => item.x < 75));
    const rowDate = parseDateValue(dateText);
    if (rowDate) {
      currentDate = rowDate;
    }

    if (
      isLikelyStatementNoiseLine(rowText) ||
      /\bbalance\s+forward\b/i.test(rowText) ||
      /\binterest\s+rate\b/i.test(rowText) ||
      /^lending\s+@/i.test(rowText) ||
      /^txndate:/i.test(rowText) ||
      /^ie\d{10,}/i.test(rowText) ||
      /\b(?:gbp|usd)@/i.test(rowText) ||
      /\bincl\s+fx\s+fee\b/i.test(rowText)
    ) {
      return;
    }

    if (!currentDate) {
      skippedRows.push({
        rowNumber: index + 1,
        reason: 'missing_date'
      });
      return;
    }

    const description = joinPdfItemText(items.filter((item) => item.x >= 70 && item.x < 250));
    if (!description || description === '.' || lowerText.includes('date details debit')) {
      return;
    }

    const debit = parsePdfAmountFromItems(items.filter((item) => item.x >= 250 && item.x < 315));
    const credit = parsePdfAmountFromItems(items.filter((item) => item.x >= 315 && item.x < 370));
    const isAibOutgoingDescription =
      /^(VDP|VDC|D\/D|FEE-|INTEREST\s+CHARGED\b|\*MOBI\s+TOP-UP\b)/i.test(description);

    let signedAmount = null;
    if (Number.isFinite(debit) && Math.abs(debit) > 0) {
      signedAmount = -Math.abs(debit);
    } else if (Number.isFinite(credit) && Math.abs(credit) > 0 && isAibOutgoingDescription) {
      signedAmount = -Math.abs(credit);
    } else if (Number.isFinite(credit) && Math.abs(credit) > 0) {
      signedAmount = Math.abs(credit);
    }

    if (!Number.isFinite(signedAmount) || signedAmount === 0) {
      return;
    }

    parsedRows.push({
      rowNumber: index + 1,
      serviceName: description,
      description,
      category: '',
      date: currentDate,
      amount: parseFloat(Math.abs(signedAmount).toFixed(2)),
      signedAmount: parseFloat(signedAmount.toFixed(2)),
      vatAmount: null,
      transactionType: signedAmount < 0 ? 'expenses' : 'income'
    });
  });

  return { parsedRows, skippedRows };
};

const getHeaderAliasMatchScore = (header, alias, aliasOrderIndex) => {
  if (!header || !alias) return Number.NEGATIVE_INFINITY;

  if (header === alias) return 1000 - aliasOrderIndex;
  if (header.startsWith(alias)) return 600 - aliasOrderIndex;
  if (header.includes(alias)) return 300 - aliasOrderIndex;

  return Number.NEGATIVE_INFINITY;
};

const detectColumnMapForAliases = (headerRow = [], aliasesMap = {}, fallbackMap = {}) => {
  const normalizedHeaders = headerRow.map((header) => normalizeHeaderKey(header));
  const columnMap = { ...fallbackMap };

  Object.entries(aliasesMap).forEach(([field, aliases]) => {
    const normalizedAliases = aliases.map((alias) => normalizeHeaderKey(alias)).filter(Boolean);

    let bestMatchIndex = -1;
    let bestScore = Number.NEGATIVE_INFINITY;

    normalizedHeaders.forEach((header, headerIndex) => {
      normalizedAliases.forEach((alias, aliasIndex) => {
        const score = getHeaderAliasMatchScore(header, alias, aliasIndex);
        if (score > bestScore) {
          bestScore = score;
          bestMatchIndex = headerIndex;
        }
      });
    });

    if (bestMatchIndex >= 0 && Number.isFinite(bestScore)) {
      columnMap[field] = bestMatchIndex;
    }
  });

  return columnMap;
};

const detectColumnMap = (headerRow = []) =>
  detectColumnMapForAliases(headerRow, HEADER_ALIASES, DEFAULT_COLUMN_MAP);

const detectBankColumnMap = (headerRow = []) =>
  detectColumnMapForAliases(headerRow, BANK_HEADER_ALIASES, {
    date: -1,
    description: -1,
    debit: -1,
    credit: -1,
    amount: -1,
    balance: -1
  });

const scoreHeaderRow = (row = []) => {
  const normalizedHeaders = row.map((header) => normalizeHeaderKey(header));
  if (!normalizedHeaders.length) return 0;

  let score = 0;
  Object.values(HEADER_ALIASES).forEach((aliases) => {
    const aliasSet = new Set(aliases.map((alias) => normalizeHeaderKey(alias)));
    const hasFieldMatch = normalizedHeaders.some((header) => {
      if (!header) return false;
      if (aliasSet.has(header)) return true;
      return aliases.some((alias) => {
        const normalizedAlias = normalizeHeaderKey(alias);
        return header.includes(normalizedAlias) || normalizedAlias.includes(header);
      });
    });

    if (hasFieldMatch) score += 1;
  });

  return score;
};

const detectHeaderRowIndex = (rows = []) => {
  const scanLimit = Math.min(rows.length, 20);
  let bestScore = 0;
  let bestIndex = -1;

  for (let index = 0; index < scanLimit; index += 1) {
    const currentRow = rows[index] || [];
    if (isEffectivelyEmptyRow(currentRow)) continue;
    const currentScore = scoreHeaderRow(currentRow);
    if (currentScore > bestScore) {
      bestScore = currentScore;
      bestIndex = index;
    }
  }

  return bestScore >= 2 ? bestIndex : -1;
};

const isSalesSummaryLayout = (headerRow = []) => {
  const normalizedHeaders = headerRow.map((header) => normalizeHeaderKey(header));
  if (!normalizedHeaders.length) return false;

  const hasType = normalizedHeaders.includes('type');
  const hasSalesQty = normalizedHeaders.includes('salesqty');
  const hasItemsSold = normalizedHeaders.includes('itemssold');
  const hasTotalSales = normalizedHeaders.includes('totalsales');

  return hasType && hasSalesQty && hasItemsSold && hasTotalSales;
};

const buildHeaderLookup = (headerRow = []) => {
  const lookup = {};
  headerRow.forEach((header, index) => {
    const key = normalizeHeaderKey(header);
    if (!key) return;
    if (lookup[key] === undefined) {
      lookup[key] = index;
    }
  });
  return lookup;
};

const getHeaderIndexByAliases = (headerLookup = {}, aliases = []) => {
  for (const alias of aliases) {
    const aliasKey = normalizeHeaderKey(alias);
    if (!aliasKey) continue;
    if (headerLookup[aliasKey] !== undefined) {
      return headerLookup[aliasKey];
    }
    const partialKey = Object.keys(headerLookup).find(
      (key) => key.includes(aliasKey) || aliasKey.includes(key)
    );
    if (partialKey && headerLookup[partialKey] !== undefined) {
      return headerLookup[partialKey];
    }
  }
  return -1;
};

const formatSummaryCount = (value) => {
  if (!Number.isFinite(value)) return '0';
  if (Number.isInteger(value)) return String(value);
  return String(Number(value.toFixed(2)));
};

const isLikelyBankStatement = (headerRow = []) => {
  const normalizedHeaders = headerRow.map((header) => normalizeHeaderKey(header));
  if (!normalizedHeaders.length) return false;

  const hasDate = normalizedHeaders.some((header) =>
    BANK_HEADER_ALIASES.date.some((alias) => {
      const aliasKey = normalizeHeaderKey(alias);
      return header === aliasKey || header.includes(aliasKey) || aliasKey.includes(header);
    })
  );

  const hasDescription = normalizedHeaders.some((header) =>
    BANK_HEADER_ALIASES.description.some((alias) => {
      const aliasKey = normalizeHeaderKey(alias);
      return header === aliasKey || header.includes(aliasKey) || aliasKey.includes(header);
    })
  );

  const hasMoneyColumn =
    normalizedHeaders.some((header) =>
      BANK_HEADER_ALIASES.amount.some((alias) => {
        const aliasKey = normalizeHeaderKey(alias);
        return header === aliasKey || header.includes(aliasKey) || aliasKey.includes(header);
      })
    ) ||
    normalizedHeaders.some((header) =>
      BANK_HEADER_ALIASES.debit.some((alias) => {
        const aliasKey = normalizeHeaderKey(alias);
        return header === aliasKey || header.includes(aliasKey) || aliasKey.includes(header);
      })
    ) ||
    normalizedHeaders.some((header) =>
      BANK_HEADER_ALIASES.credit.some((alias) => {
        const aliasKey = normalizeHeaderKey(alias);
        return header === aliasKey || header.includes(aliasKey) || aliasKey.includes(header);
      })
    );

  return hasDate && hasDescription && hasMoneyColumn;
};

const parseSalesRows = (
  rows = [],
  startIndex = 0,
  columnMap = DEFAULT_COLUMN_MAP,
  options = {}
) => {
  const fallbackDate = options?.fallbackDate || '';
  const allowMissingDate = options?.allowMissingDate === true;
  const parsedRows = [];
  const skippedRows = [];

  for (let rowIndex = startIndex; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex] || [];
    if (isEffectivelyEmptyRow(row)) continue;

    const serviceNameRaw = row[columnMap.serviceName];
    const sourceTypeRaw = columnMap.sourceType >= 0 ? row[columnMap.sourceType] : '';
    const categoryRaw = row[columnMap.category];
    const dateRaw = row[columnMap.date];
    const amountRaw = row[columnMap.amount];
    const vatRaw = row[columnMap.vatAmount];

    const serviceName = hasMeaningfulValue(serviceNameRaw) ? String(serviceNameRaw).trim() : '';
    const sourceType = hasMeaningfulValue(sourceTypeRaw) ? String(sourceTypeRaw).trim() : '';
    const category = hasMeaningfulValue(categoryRaw) ? String(categoryRaw).trim() : '';
    const date = parseDateValue(dateRaw);
    const amount = parseNumberValue(amountRaw);
    const vatAmount = parseNumberValue(vatRaw);

    const resolvedDate = date || (allowMissingDate ? fallbackDate : '');

    if (!resolvedDate) {
      skippedRows.push({
        rowNumber: rowIndex + 1,
        reason: 'missing_date'
      });
      continue;
    }

    if (!Number.isFinite(amount) || amount === 0) {
      skippedRows.push({
        rowNumber: rowIndex + 1,
        reason: 'missing_amount'
      });
      continue;
    }

    parsedRows.push({
      rowNumber: rowIndex + 1,
      sourceType,
      serviceName,
      category,
      date: resolvedDate,
      amount: parseFloat(amount.toFixed(2)),
      vatAmount: Number.isFinite(vatAmount) ? parseFloat(vatAmount.toFixed(2)) : null
    });
  }

  return { parsedRows, skippedRows };
};

const parseSalesSummaryRows = (rows = [], startIndex = 0, headerRow = [], options = {}) => {
  const parsedRows = [];
  const skippedRows = [];
  const fallbackDate = options?.fallbackDate || formatDateAsIso(new Date()) || '';
  const headerLookup = buildHeaderLookup(headerRow);

  const typeIndex = getHeaderIndexByAliases(headerLookup, ['type']);
  const salesQtyIndex = getHeaderIndexByAliases(headerLookup, ['sales qty', 'salesqty']);
  const itemsSoldIndex = getHeaderIndexByAliases(headerLookup, ['items sold', 'itemssold']);
  const totalSalesIndex = getHeaderIndexByAliases(headerLookup, ['total sales', 'totalsales']);
  const netSalesIndex = getHeaderIndexByAliases(headerLookup, ['net sales', 'netsales']);
  const grossSalesIndex = getHeaderIndexByAliases(headerLookup, ['gross sales', 'grosssales']);
  const taxesIndex = getHeaderIndexByAliases(headerLookup, ['taxes', 'tax']);

  for (let rowIndex = startIndex; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex] || [];
    if (isEffectivelyEmptyRow(row)) continue;

    const typeRaw = typeIndex >= 0 ? row[typeIndex] : '';
    const salesQty = salesQtyIndex >= 0 ? parseNumberValue(row[salesQtyIndex]) : null;
    const itemsSold = itemsSoldIndex >= 0 ? parseNumberValue(row[itemsSoldIndex]) : null;
    const totalSales = totalSalesIndex >= 0 ? parseNumberValue(row[totalSalesIndex]) : null;
    const netSales = netSalesIndex >= 0 ? parseNumberValue(row[netSalesIndex]) : null;
    const grossSales = grossSalesIndex >= 0 ? parseNumberValue(row[grossSalesIndex]) : null;
    const taxes = taxesIndex >= 0 ? parseNumberValue(row[taxesIndex]) : null;

    const amountCandidate = [totalSales, netSales, grossSales].find(
      (value) => Number.isFinite(value)
    );

    if (!Number.isFinite(amountCandidate) || amountCandidate === 0) {
      skippedRows.push({
        rowNumber: rowIndex + 1,
        reason: 'missing_amount'
      });
      continue;
    }

    const typeLabel = hasMeaningfulValue(typeRaw) ? String(typeRaw).trim() : 'Sales';
    const quantityLabel = Number.isFinite(salesQty) ? formatSummaryCount(salesQty) : '0';
    const itemsLabel = Number.isFinite(itemsSold) ? formatSummaryCount(itemsSold) : '0';
    const summaryDescription = `${quantityLabel} transactions with ${itemsLabel} items sold`;

    parsedRows.push({
      rowNumber: rowIndex + 1,
      sourceType: typeLabel,
      serviceName: summaryDescription,
      category: typeLabel,
      date: fallbackDate,
      amount: parseFloat(amountCandidate.toFixed(2)),
      vatAmount:
        Number.isFinite(taxes) && taxes > 0
          ? parseFloat(taxes.toFixed(2))
          : null
    });
  }

  return { parsedRows, skippedRows };
};

const parseBankStatementRows = (rows = [], startIndex = 0, columnMap = {}) => {
  const parsedRows = [];
  const skippedRows = [];

  for (let rowIndex = startIndex; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex] || [];
    if (isEffectivelyEmptyRow(row)) continue;

    const dateRaw = columnMap.date >= 0 ? row[columnMap.date] : '';
    const descriptionRaw = columnMap.description >= 0 ? row[columnMap.description] : '';
    const debitRaw = columnMap.debit >= 0 ? row[columnMap.debit] : '';
    const creditRaw = columnMap.credit >= 0 ? row[columnMap.credit] : '';
    const amountRaw = columnMap.amount >= 0 ? row[columnMap.amount] : '';

    const date = parseDateValue(dateRaw);
    if (!date) {
      skippedRows.push({
        rowNumber: rowIndex + 1,
        reason: 'missing_date'
      });
      continue;
    }

    const description = hasMeaningfulValue(descriptionRaw)
      ? String(descriptionRaw).trim()
      : 'Bank transaction';

    const debit = parseNumberValue(debitRaw);
    const credit = parseNumberValue(creditRaw);
    const amount = parseNumberValue(amountRaw);

    let signedAmount = null;
    if (Number.isFinite(debit) && Math.abs(debit) > 0) {
      signedAmount = -Math.abs(debit);
    } else if (Number.isFinite(credit) && Math.abs(credit) > 0) {
      signedAmount = Math.abs(credit);
    } else if (Number.isFinite(amount) && Math.abs(amount) > 0) {
      signedAmount = amount;
    }

    if (!Number.isFinite(signedAmount) || signedAmount === 0) {
      skippedRows.push({
        rowNumber: rowIndex + 1,
        reason: 'missing_amount'
      });
      continue;
    }

    parsedRows.push({
      rowNumber: rowIndex + 1,
      serviceName: description,
      description,
      category: '',
      date,
      amount: parseFloat(Math.abs(signedAmount).toFixed(2)),
      signedAmount: parseFloat(signedAmount.toFixed(2)),
      vatAmount: null,
      transactionType: signedAmount < 0 ? 'expenses' : 'income'
    });
  }

  return { parsedRows, skippedRows };
};

const parsePdfBankStatementRows = (textRows = []) => {
  const positionedResult = parsePositionedPdfBankStatementRows(textRows);
  if (positionedResult && positionedResult.parsedRows.length > 0) {
    return positionedResult;
  }

  const statementLines = normalizePdfStatementLines(textRows);
  const parsedRows = [];
  const skippedRows = [];

  statementLines.forEach((line, index) => {
    const match = line.text.match(PDF_DATE_AT_START_PATTERN);
    if (!match) {
      skippedRows.push({
        rowNumber: line.rowNumber || index + 1,
        reason: 'missing_date'
      });
      return;
    }

    const date = parseDateValue(match[1]);
    if (!date) {
      skippedRows.push({
        rowNumber: line.rowNumber || index + 1,
        reason: 'missing_date'
      });
      return;
    }

    const remainder = String(match[2] || '').trim();
    const amountMatches = remainder.match(PDF_AMOUNT_TOKEN_PATTERN) || [];
    const signedAmount = chooseSignedAmountFromPdfTokens(amountMatches);

    if (!Number.isFinite(signedAmount) || signedAmount === 0) {
      skippedRows.push({
        rowNumber: line.rowNumber || index + 1,
        reason: 'missing_amount'
      });
      return;
    }

    const amountTokensToStrip = amountMatches.slice(-3);
    const description = stripPdfAmountTokensFromEnd(remainder, amountTokensToStrip) ||
      'Bank transaction';

    parsedRows.push({
      rowNumber: line.rowNumber || index + 1,
      serviceName: description,
      description,
      category: '',
      date,
      amount: parseFloat(Math.abs(signedAmount).toFixed(2)),
      signedAmount: parseFloat(signedAmount.toFixed(2)),
      vatAmount: null,
      transactionType: signedAmount < 0 ? 'expenses' : 'income'
    });
  });

  return { parsedRows, skippedRows };
};

const parsePdfStatementTransactions = async (file) => {
  const textRows = await extractTextRowsFromPdfFile(file, { maxPages: 24 });
  const parsedResult = parsePdfBankStatementRows(textRows);

  if (parsedResult.parsedRows.length === 0 && parsedResult.skippedRows.length === 0) {
    throw new Error('No selectable text was found in this PDF. Scanned statement PDFs are not supported yet.');
  }

  return {
    sheetName: 'PDF statement',
    sourceKind: 'bank_statement',
    sourceVariant: PDF_STATEMENT_SOURCE_VARIANT,
    parsedRows: parsedResult.parsedRows,
    skippedRows: parsedResult.skippedRows,
    columnMap: {
      date: 0,
      description: 1,
      debit: -1,
      credit: -1,
      amount: 2,
      balance: -1
    },
    headerRowIndex: -1
  };
};

export const parseSpreadsheetTransactions = async (file) => {
  if (!file) {
    throw new Error('No file was provided.');
  }

  if (isPdfFile(file)) {
    return parsePdfStatementTransactions(file);
  }

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, {
    type: 'array',
    cellDates: true,
    raw: true
  });

  if (!Array.isArray(workbook.SheetNames) || workbook.SheetNames.length === 0) {
    throw new Error('The spreadsheet does not contain any sheets.');
  }

  const sheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[sheetName];

  if (!worksheet) {
    throw new Error('Could not read the first worksheet from this file.');
  }

  const rows = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    defval: '',
    blankrows: false,
    raw: true
  });

  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      sheetName,
      parsedRows: [],
      skippedRows: [],
      columnMap: { ...DEFAULT_COLUMN_MAP },
      headerRowIndex: -1
    };
  }

  const headerRowIndex = detectHeaderRowIndex(rows);
  const headerRow = headerRowIndex >= 0 ? (rows[headerRowIndex] || []) : [];
  const sourceKind = isLikelyBankStatement(headerRow) ? 'bank_statement' : 'sales_report';
  const sourceVariant = sourceKind === 'sales_report' && isSalesSummaryLayout(headerRow)
    ? 'sales_summary'
    : 'default';
  const fallbackDateFromFile = extractIsoDateFromFileName(file?.name || '');
  const columnMap =
    headerRowIndex >= 0
      ? (sourceKind === 'bank_statement' ? detectBankColumnMap(headerRow) : detectColumnMap(headerRow))
      : { ...DEFAULT_COLUMN_MAP };
  const startIndex = headerRowIndex >= 0 ? headerRowIndex + 1 : 0;

  const parsedResult =
    sourceKind === 'bank_statement'
      ? parseBankStatementRows(rows, startIndex, columnMap)
      : sourceVariant === 'sales_summary'
        ? parseSalesSummaryRows(rows, startIndex, headerRow, {
            fallbackDate: fallbackDateFromFile || formatDateAsIso(new Date()) || ''
          })
        : parseSalesRows(rows, startIndex, columnMap, {
            fallbackDate: fallbackDateFromFile || '',
            allowMissingDate: false
          });

  return {
    sheetName,
    sourceKind,
    sourceVariant,
    parsedRows: parsedResult.parsedRows,
    skippedRows: parsedResult.skippedRows,
    columnMap,
    headerRowIndex
  };
};

export default parseSpreadsheetTransactions;
